import React, { useState, useRef, useMemo } from 'react';
import { NextBtn, BackBtn } from './ServicioUI';
import { useAuth } from '../../context/AuthContext';
import RepuestoRapidoModal from '../repuesto/RepuestoRapidoModal';
import RepuestosBottomSheet from '../repuesto/RepuestosBottomSheet';
import TicketItemsList from './TicketItemsList';
import CalculadoraMO from './CalculadoraMO';
import FormEquipoTicket from './FormEquipoTicket';
import { LuShieldCheck, LuHourglass } from 'react-icons/lu';
import { generarSerie as generarSerieAuto } from '../../utils/serie';
import { toast } from 'react-hot-toast';

export default function PasoEquipos({ hook, onNext, onBack, selectStyles }) {
    const {
        db, setDb, clienteId, ticketItems, setTicketItems,
        itemActual, setItemActual,
        actualizarCantidad, quitarRepuesto,
        agregarAlTicket, calcularGananciaRepuesto,
        consultarAntecedentes, historialEquipo,
        configGlobal, editarItem, eliminarItem,
        tecnicoSeleccionado,
    } = hook;

    const moBase = Number(configGlobal?.manoDeObraBase) || 72600;
    const pctImp = Number(configGlobal?.porcentajeImpuestos) || 30;
    const pctIVA = Number(configGlobal?.porcentajeIVA) || 21;
    // moBase ya incluye IVA
    const precioReparacion = moBase;
    const precioVisita = Math.round(moBase / 2);
    const [tipoMO, setTipoMO] = useState('REPARACION');
    const { esAdmin, usuario } = useAuth();
    const [generandoSerie, setGenerandoSerie] = useState(false);

    // N/S automático: inicial del técnico + "S" (Service) + fecha ddmmaa, y si ya
    // existe el backend le agrega A, B, C... (ej. Marcos hoy → MS290926, MS290926A).
    const generarSerie = async () => {
        const nombre = (tecnicoSeleccionado?.nombre || usuario?.nombre || 'X').trim();
        setGenerandoSerie(true);
        try {
            const serie = await generarSerieAuto(nombre, ticketItems.map(i => i.equipoSerial));
            // Actualización funcional: "Igual que el anterior" la llama justo después de copiar el trabajo
            setItemActual(it => ({ ...it, equipoSerial: serie, esNuevoEquipo: true, nsGenerado: true }));
            setMostrarEquipo(true);
            toast.success(`N/S generado: ${serie}`);
        } catch {
            toast.error('No se pudo generar el N/S');
        } finally {
            setGenerandoSerie(false);
        }
    };
    const [mostrarEquipo, setMostrarEquipo] = useState(false);
    const [formVisible, setFormVisible] = useState(true);
    const [modalRepuesto, setModalRepuesto] = useState(false);
    const [nombreRepuesto, setNombreRepuesto] = useState('');
    const [sheetRepuestos, setSheetRepuestos] = useState(false);

    const prevSerial = useRef(itemActual.equipoSerial);
    React.useEffect(() => {
        const tieneContenido = itemActual.equipoSerial || itemActual.repuestosUsados?.length > 0;
        if (tieneContenido && itemActual.equipoSerial !== prevSerial.current) setFormVisible(true);
        prevSerial.current = itemActual.equipoSerial;
    }, [itemActual]);

    const esVisitaActual = tipoMO === 'VISITA';
    const divisor = esVisitaActual ? 1 : 2;
    const desglose = useMemo(() => {
        const precioConIVA = parseFloat(itemActual.costoExtra) || 0;
        const sinIVA = Math.round(precioConIVA / (1 + pctIVA / 100));
        const netoFactura = Math.round(precioConIVA * (1 - pctImp / 100));
        return {
            precioCliente: precioConIVA, efectivoTotal: sinIVA,
            efectivoCada: Math.round(sinIVA / divisor),
            facturaCliente: precioConIVA, facturaNeto: netoFactura,
            facturaCada: Math.round(netoFactura / divisor),
        };
    }, [itemActual.costoExtra, pctIVA, pctImp, divisor]);

    const equiposInventario = (() => {
        if (!clienteId || !db.sedes?.length || !db.equipos?.length) return [];
        const ids = db.sedes.filter(s => s.cliente?.id?.toString() === clienteId).map(s => s.id);
        return db.equipos.filter(e => ids.includes(e.sedeId));
    })();

    const serialesHistorial = (() => {
        if (!clienteId || !db.servicios?.length) return [];
        const clienteObj = db.clientes?.find(c => c.id?.toString() === clienteId);
        if (!clienteObj) return [];
        return [...new Set(
            db.servicios.filter(s => s.clienteNombre === clienteObj.nombre)
                .flatMap(s => s.items?.map(i => i.equipoSerial).filter(Boolean) || [])
                .filter(s => s !== 'MOSTRADOR')
        )];
    })();

    const opcionesSerial = [
        ...equiposInventario.map(e => ({
            value: e.numeroSerie,
            label: `S/N: ${e.numeroSerie}${e.modelo ? ` — ${e.modelo}` : ''}`,
        })),
        ...serialesHistorial
            .filter(s => !equiposInventario.some(e => e.numeroSerie === s))
            .map(s => ({ value: s, label: `S/N: ${s} (historial)` })),
    ];

    return (
        <div className="flex flex-col gap-4 px-5 pb-6">
            <TicketItemsList ticketItems={ticketItems} editarItem={editarItem} eliminarItem={eliminarItem} onReorder={setTicketItems} />

            {!formVisible && ticketItems.length > 0 ? (
                <button type="button" onClick={() => setFormVisible(true)}
                    className="w-full py-3.5 rounded-2xl font-black text-label uppercase border-2 border-dashed border-[#D13A28]/40 dark:border-[#E8422F]/40 text-brand-red bg-transparent active:scale-[0.98] transition-all">
                    + Agregar equipo
                </button>
            ) : (
            <FormEquipoTicket numero={ticketItems.length + 1}
                itemActual={itemActual} setItemActual={setItemActual} selectStyles={selectStyles}
                opcionesSerial={opcionesSerial}
                onElegirSerie={s => {
                    if (!s) { setItemActual({ ...itemActual, equipoSerial: '', esNuevoEquipo: false }); return; }
                    const equipoDB = db.equipos?.find(e => e.numeroSerie === s.value);
                    setItemActual({ ...itemActual, equipoSerial: s.value, esNuevoEquipo: false,
                        modeloEquipo: itemActual.modeloEquipo || equipoDB?.modelo || '',
                        ubicacionEquipo: itemActual.ubicacionEquipo || equipoDB?.ubicacion || '' });
                    consultarAntecedentes(s.value);
                }}
                onCrearSerie={async serie => {
                    const ok = await consultarAntecedentes(serie);
                    if (ok === false) return; // es de otro cliente: no se carga
                    setItemActual({ ...itemActual, equipoSerial: serie, esNuevoEquipo: true });
                }}
                onGenerarSerie={generarSerie} generandoSerie={generandoSerie}
                mostrarEquipo={mostrarEquipo} setMostrarEquipo={setMostrarEquipo}
                historial={historialEquipo && (() => {
                                    // Lo primero que se lee es si está en garantía (dato que decide
                                    // si se cobra); abajo, cuándo fue el último servicio y qué se hizo.
                                    const g = historialEquipo.garantiaInfo;
                                    const dd = (iso) => { const [y, m, d] = String(iso).slice(0, 10).split('-'); return `${d}/${m}/${y}`; };
                                    const plural = (n, pal) => `${n} ${pal}${n === 1 ? '' : 's'}`;
                                    const hace = (n) => n == null ? '' : n === 0 ? 'hoy' : n < 60 ? `hace ${plural(n, 'día')}` : `hace ${Math.round(n / 30)} meses`;
                                    const vigente = g?.vigente;
                                    return (
                                        <div className={`p-3 rounded-xl border ${vigente
                                            ? 'bg-[var(--success-bg)] border-[var(--estado-listo)]'
                                            : 'bg-panel border-black/10 dark:border-line'}`}>
                                            {g ? (
                                                vigente ? (
                                                    <p className="text-body-lg font-black text-[var(--success-tx)] flex items-center gap-1.5">
                                                        <LuShieldCheck size={16} /> En garantía · quedan {plural(g.dias, 'día')}
                                                    </p>
                                                ) : (
                                                    <p className="text-body-lg font-black text-ink flex items-center gap-1.5">
                                                        <LuHourglass size={16} className="text-muted" /> Sin garantía · venció {hace(Math.abs(g.dias))}
                                                    </p>
                                                )
                                            ) : (
                                                <p className="text-body-lg font-black text-ink">Sin trabajos realizados registrados</p>
                                            )}
                                            {g && (
                                                <p className="text-caption text-muted mt-0.5">
                                                    {vigente ? 'Hasta' : 'Vencía el'} {dd(g.hasta)}{g.estimada ? ' · estimada (3 meses desde el servicio)' : ''}
                                                </p>
                                            )}
                                            <p className="text-caption text-secondary mt-2">
                                                <span className="font-bold">Último {historialEquipo.esPresupuesto ? 'presupuesto' : 'servicio'}:</span>{' '}
                                                {dd(historialEquipo.fecha)} ({hace(historialEquipo.diasDesde)})
                                                {historialEquipo.items?.[0]?.trabajoRealizado ? ` — ${historialEquipo.items[0].trabajoRealizado}` : ''}
                                            </p>
                                        </div>
                                    );
                                })()}
                onAbrirRepuestos={() => setSheetRepuestos(true)}
                precioRepuesto={r => calcularGananciaRepuesto(r, r.cantidad).subtotal}
                ayudaFotos={'¿El cliente te mandó fotos? Cargalas y tocá «¿Qué es?» para ponerles rótulo (ej. "Antes · frente"). Pasan al trabajo.'}
                precio={
                    <div className="rounded-2xl p-4 bg-[#1C1917] dark:bg-[#0F0F0F] space-y-3">
                        <div className="flex gap-2">
                            <button type="button"
                                onClick={() => { setTipoMO('REPARACION'); setItemActual({ ...itemActual, costoExtra: precioReparacion, esVisita: false }); }}
                                className={`flex-1 py-2.5 rounded-xl text-label font-black uppercase transition-all active:scale-95 ${tipoMO === 'REPARACION' ? 'bg-[#D13A28] text-white' : 'bg-[#2A2A28] text-[#9E9A94]'}`}>
                                Reparación · ${precioReparacion.toLocaleString('es-AR')}
                            </button>
                            <button type="button"
                                onClick={() => { setTipoMO('VISITA'); setItemActual({ ...itemActual, costoExtra: precioVisita, esVisita: true }); }}
                                className={`flex-1 py-2.5 rounded-xl text-label font-black uppercase transition-all active:scale-95 ${tipoMO === 'VISITA' ? 'bg-[#D48800] text-white' : 'bg-[#2A2A28] text-[#9E9A94]'}`}>
                                Visita · ${precioVisita.toLocaleString('es-AR')}
                            </button>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-label font-black text-[#5C5954] uppercase tracking-widest">MO $ <span className="text-label text-muted normal-case">(con IVA)</span></span>
                            <input type="text" inputMode="decimal"
                                value={itemActual.costoExtra}
                                onChange={e => setItemActual({ ...itemActual, costoExtra: e.target.value === '' ? '' : Math.max(0, parseFloat(e.target.value) || 0) })}
                                onFocus={e => e.target.select()} placeholder="0"
                                className="flex-1 bg-transparent border-none text-white text-3xl font-black outline-none placeholder-[#666]" />
                        </div>
                    </div>}
                extra={esAdmin && (
                    <CalculadoraMO desglose={desglose} esVisita={esVisitaActual}
                        pctIVA={pctIVA}
                        itemActual={itemActual} setItemActual={setItemActual} />
                )}
                anterior={ticketItems.length ? ticketItems[ticketItems.length - 1] : null}
                extraCopia={a => { setTipoMO(a.esVisita ? 'VISITA' : 'REPARACION'); return { costoExtra: a.costoExtra, esVisita: !!a.esVisita }; }}
                onAgregar={() => { agregarAlTicket(); setMostrarEquipo(false); setFormVisible(false); }} />
            )}

            {ticketItems.length > 0 && (
                <NextBtn onClick={onNext}>
                    Ver resumen ({ticketItems.length} equipo{ticketItems.length > 1 ? 's' : ''})
                </NextBtn>
            )}
            <BackBtn onClick={onBack} />

            <RepuestosBottomSheet isOpen={sheetRepuestos} onClose={() => setSheetRepuestos(false)}
                repuestos={db.repuestos || []} seleccionados={itemActual.repuestosUsados || []}
                onChange={nuevos => setItemActual(prev => ({ ...prev, repuestosUsados: nuevos }))}
                onCrearNuevo={(nombre) => { setNombreRepuesto(nombre || ''); setSheetRepuestos(false); setModalRepuesto(true); }} />
            <RepuestoRapidoModal isOpen={modalRepuesto} onClose={() => setModalRepuesto(false)}
                nombreInicial={nombreRepuesto}
                onCreado={repuesto => { setDb(prev => ({ ...prev, repuestos: [...(prev.repuestos || []), repuesto] })); setSheetRepuestos(true); }} />
        </div>
    );
}
